/**
 * The dictation-engine choice as the user sees it.
 *
 * The listening counterpart of `voice-engine-options.ts`. Unlike the speaking
 * choice, this one is not a matter of taste: it trades a real capability for a
 * real guarantee, and each one-line hint has to say so plainly rather than
 * selling one engine as strictly better. Someone who picks Whisper and then
 * finds their words no longer appear as they speak should have been told that
 * here.
 *
 * The trade differs by platform. On Windows the system recogniser is
 * Microsoft's online service, so the choice is live-but-online against
 * private. On macOS both engines run on the Mac — Apple's recogniser in
 * on-device-only mode — so the choice is live against Whisper's accuracy. An
 * Intel Mac has no choice: Apple's recogniser is unusable there, so it
 * dictates with Whisper alone (`whisper_only` in dictation_commands.rs).
 */
import type { SelectFieldOption } from "@/components/ui";
import { translate } from "@/i18n";
import { desktopDictationMessages } from "@/i18n/messages/desktop-dictation";
import { invoke } from "@tauri-apps/api/core";

import { isIntelMac } from "@/lib/cpu-arch";
import { isTauri } from "@/lib/is-tauri";
import { isMacPlatform, isWindowsPlatform } from "@/lib/os-platform";

export type DictationEngineChoice = "system" | "whisper";

export type DictationEngineOption = {
  id: DictationEngineChoice;
  label: string;
  /** One line, shown under the Engine row while this option is chosen. */
  hint: string;
};

const WINDOWS_OPTIONS: DictationEngineOption[] = [
  {
    id: "system",
    label: "Windows",
    get hint() {
      return translate(desktopDictationMessages, "engineWindowsHint");
    },
  },
  {
    id: "whisper",
    label: "Whisper",
    get hint() {
      return translate(desktopDictationMessages, "engineWhisperWindowsHint");
    },
  },
];

const MAC_OPTIONS: DictationEngineOption[] = [
  {
    id: "system",
    label: "Apple",
    get hint() {
      return translate(desktopDictationMessages, "engineAppleHint");
    },
  },
  {
    id: "whisper",
    label: "Whisper",
    get hint() {
      return translate(desktopDictationMessages, "engineWhisperMacHint");
    },
  },
];

/** The engines this machine offers, platform recogniser first: Whisper alone
 *  on an Intel Mac. */
export function dictationEngineOptions(
  windows = isWindowsPlatform(),
): DictationEngineOption[] {
  if (windows) return WINDOWS_OPTIONS;
  return isIntelMac()
    ? MAC_OPTIONS.filter((option) => option.id === "whisper")
    : MAC_OPTIONS;
}

/** An engine as an Engine menu entry, its one line as the description; the
 *  menu lists the local engine as its models (`use-dictation-engine-menu.ts`)
 *  once they are known. */
export function engineMenuOption(
  option: DictationEngineOption,
): SelectFieldOption {
  return { value: option.id, label: option.label, description: option.hint };
}

/**
 * Whether to offer the choice at all: on both desktop platforms, which each
 * have their own recogniser and Whisper. The web build has no dictation of its
 * own to switch.
 */
export function isDictationEngineSelectable(): boolean {
  return isTauri() && (isWindowsPlatform() || isMacPlatform());
}

/** The engine this machine uses when the user has expressed no preference -
 *  the only one, on an Intel Mac. */
export async function machineDefaultDictationEngine(): Promise<DictationEngineChoice> {
  if (!isTauri()) return "system";
  if (isIntelMac()) return "whisper";
  try {
    const engine = await invoke<string>("denker_dictation_engine");
    return engine === "whisper" ? "whisper" : "system";
  } catch {
    return "system";
  }
}

/**
 * Show the macOS microphone prompt for Denker itself as Whisper is picked.
 * Whisper records in the app rather than in the Apple speech helper, which
 * holds its own grant; asking here keeps the prompt out of the first
 * dictation. Nothing to ask anywhere else.
 */
export function requestWhisperMicrophone(): void {
  if (!isTauri() || !isMacPlatform()) return;
  void invoke("denker_whisper_request_microphone").catch(() => {});
}
